import React from 'react';
import { Alert, CircularProgress, type ModalProps } from '@mui/material';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';

import { useMarkInvoiceAsSentMutation } from 'generated/graphql';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { showMessageAction } from 'containers/Notification/actions';

export const MarkInvoiceAsSentModal = ({
  invoiceId,
  style,
  onClickClose = (): void => {},
  ...props
}: {
  invoiceId: string;
  onClickClose: () => void;
} & Omit<ModalProps, 'children'>): JSX.Element => {
  const dispatch = useDispatch();
  const [
    markInvoiceAsSentMutation,
    { loading: markInvoiceAsSentMutationLoading, error: markInvoiceAsSentMutationError },
  ] = useMarkInvoiceAsSentMutation({
    refetchQueries: ['givingAccountSummary', 'givingAccountTransactions', 'invoices'],
    onCompleted: () => {
      dispatch(showMessageAction('Invoice marked as sent successfully.'));
    },
  });

  const handleSubmit = async () => {
    await markInvoiceAsSentMutation({
      variables: {
        invoiceId,
      },
    });
    onClickClose();
  };

  return (
    <Modal
      disableAutoFocus
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      {...props}
    >
      <ModalContents
        onClickClose={onClickClose}
        body={
          <>
            <ModalTitle>Mark Invoice as Sent</ModalTitle>
            <Box paddingTop={4}>Are you sure you want to mark the invoice as sent?</Box>
            {markInvoiceAsSentMutationError && (
              <Alert severity="error">
                Failed to mark the invoice as sent:
                <br />
                <br />
                {markInvoiceAsSentMutationError?.message}
              </Alert>
            )}
          </>
        }
        actions={
          <>
            <ModalButton
              variant="outlined"
              disabled={markInvoiceAsSentMutationLoading}
              onClick={(): void => {
                onClickClose();
              }}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton disabled={markInvoiceAsSentMutationLoading} onClick={handleSubmit}>
              {markInvoiceAsSentMutationLoading ? <CircularProgress size={21} /> : 'Mark as Sent'}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};
